import { Navigate, useOutletContext } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useStrictParams } from 'modules/core/hooks/useStrictParams'
import users, { questionGroupRespondent } from 'modules/core/store/collections/users'

import contractVersions from 'modules/contracts/store/collections/contractVersions'
import { isManager } from 'modules/employees/lib/utils'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import SurveyAnswer from 'modules/surveys/components/Answer/SurveyAnswer'
import SurveyShowHeader from 'modules/surveys/components/Show/SurveyShowHeader'
import SurveyShowPageTabs from 'modules/surveys/components/Show/SurveyShowPageTabs'
import { useQuestionGroup } from 'modules/surveys/hooks/useQuestionGroup'
import { useSurveyShowContext } from 'modules/surveys/pages/Show/hooks'
import type SurveyForm from 'modules/surveys/store/models/Form'
import type QuestionGroup from 'modules/surveys/store/models/QuestionGroup'
import memberships from 'modules/teams/store/collections/memberships'
import teams from 'modules/teams/store/collections/teams'
import { userIsTeamLead } from 'modules/teams/store/userIsTeamLead'

const RedirectToResponses = () => {
  const { navigation } = useNavigation()
  const { id } = useStrictParams<'id'>()
  const to = navigation.engagement.surveys.id.responses.fullPath({ id })

  return <Navigate to={to} />
}

const AnswerPage = () => {
  const {
    employee,
    access: { id: userId },
  } = useCurrentSession()
  const { survey, canEditSurvey } = useSurveyShowContext()
  const { group } = useOutletContext<{ form: SurveyForm; group?: QuestionGroup }>()
  const id = group?.attributes.id

  const { loading, questionGroup, refresh } = useQuestionGroup(Number(id))

  const { loading: loadingContractVersions } = useAsyncData(
    () => contractVersions.fetch({ withCachedFetchParams: true }),
    []
  )

  if (!survey || !questionGroup) {
    return null
  }

  const isQuestionGroupRespondent = questionGroup
    ? questionGroupRespondent(questionGroup)?.id === userId
    : false

  const canSeeInfo =
    survey.attributes.visible_to_managers &&
    (isManager(employee) ||
      (users.current.employee
        ? userIsTeamLead(teams, memberships, users.current.employee.id)
        : false))

  if (!isQuestionGroupRespondent && canSeeInfo) return <RedirectToResponses />

  return (
    <ResponsivePage.Body
      showHeader={false}
      loading={loading || loadingContractVersions}
      error={null}
      authorized={isQuestionGroupRespondent}
    >
      <Box gap="s24">
        <SurveyShowHeader survey={survey} canEditSurvey={canEditSurvey} />
        <SurveyShowPageTabs form={survey} canEditSurvey={canEditSurvey} />
      </Box>
      <SurveyAnswer
        loading={loading}
        questionGroup={questionGroup}
        onDraftSaved={refresh}
        form={survey}
        canEditSurvey={canEditSurvey}
        contractVersions={contractVersions}
      />
    </ResponsivePage.Body>
  )
}

export default AnswerPage
